import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, FileText, Trash2, ScanText, Loader2, Sparkles } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";

export default function UploadPanel({ caseId, documents, onChange, onAnalyze, analyzing, stageCode, title, analyzeLabel }) {
  const { t } = useI18n();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    for (const file of Array.from(files)) {
      const fd = new FormData();
      fd.append("file", file);
      if (stageCode) fd.append("stage_code", stageCode);
      try {
        await api.post(`/cases/${caseId}/documents`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success(t("upload.uploaded", { name: file.name }));
      } catch (e) { toast.error(apiErr(e)); }
    }
    setUploading(false);
    onChange();
  };

  const del = async (id) => {
    try { await api.delete(`/cases/${caseId}/documents/${id}`); onChange(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="rounded-xl border border-[#2a2a35] bg-[#18181d] overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
        <h3 className="font-mono-ui text-[11px] uppercase tracking-[0.15em] text-zinc-400">
          {title || t("ws.allFiles")} · {documents.length}
        </h3>
        <Button
          data-testid={stageCode ? "analyze-stage-btn" : "analyze-btn"}
          onClick={onAnalyze}
          disabled={analyzing || documents.length === 0}
          className="h-9 rounded-full bg-cyan-500/15 text-cyan-300 border border-cyan-500/30 hover:bg-cyan-500/25 px-4"
        >
          {analyzing ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Sparkles className="h-4 w-4 mr-1.5" />}
          {analyzing ? t("upload.analyzing") : (analyzeLabel || t("upload.analyzeAI"))}
        </Button>
      </div>

      <div className="p-5">
        <div
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
          data-testid="upload-dropzone"
          className="cursor-pointer rounded-lg border border-dashed border-zinc-700 hover:border-zinc-500 bg-[#0a0a0c]/40 p-6 text-center transition-colors"
        >
          <input
            ref={inputRef} type="file" multiple hidden
            data-testid="file-input"
            accept=".pdf,.docx,.png,.jpg,.jpeg,.webp,.txt"
            onChange={(e) => handleFiles(e.target.files)}
          />
          {uploading ? (
            <Loader2 className="mx-auto h-6 w-6 animate-spin text-zinc-500" />
          ) : (
            <Upload className="mx-auto h-6 w-6 text-zinc-600" strokeWidth={1.5} />
          )}
          <p className="mt-3 text-sm text-zinc-400">{t("upload.drop")}</p>
          <p className="mt-1 font-mono-ui text-[11px] text-zinc-600">{t("upload.hint")}</p>
        </div>

        {documents.length > 0 && (
          <ul className="mt-4 space-y-2">
            {documents.map((d) => (
              <li key={d.id} data-testid={`doc-${d.id}`}
                className="group flex items-center gap-3 rounded-md border border-zinc-800 bg-[#0a0a0c]/40 px-3 py-2.5">
                <FileText className="h-4 w-4 text-zinc-500 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm text-zinc-300">{d.filename}</p>
                  <p className="font-mono-ui text-[10px] text-zinc-600">
                    {t("upload.pages", { n: d.page_count })} · {(d.size / 1024).toFixed(0)} KB
                  </p>
                </div>
                {d.ocr_used && (
                  <span className="flex items-center gap-1 font-mono-ui text-[10px] text-emerald-400/80">
                    <ScanText className="h-3 w-3" /> OCR
                  </span>
                )}
                <button onClick={() => del(d.id)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity text-zinc-500 hover:text-rose-400">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
